// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import {jsx} from 'panel';

import {ButtonEl} from 'common/components/button';
import {ModalEl, VerticalAlignment} from 'common/components/modal';
import {SelectEl} from 'common/components/select';
import {FileUploaderEl} from 'common/components/file-uploader';
import {ButtonTheme} from 'common/components/button/constants';
import {SvgIconEl} from 'common/components/svg-icon';
import {ConfirmEl} from 'common/components/confirm';
import {THEME} from 'common/components/confirm/types';
// oxlint-disable-next-line no-cycle
import {QueryBlockEl} from 'common/query-builder/query-block/panel';

import type {MapLookupTableModal} from './index';

export function template(this: MapLookupTableModal) {
  const {state, helpers} = this;
  const {t} = this.getContext(`i18n`);
  return (
    <div sel='.map-lookup-table-modal'>
      <ModalEl
        attrs={{
          open: this.attr(`open`),
          closeable: true,
          'grow-to-content': true,
          'vertical-alignment': this.attr(`type`) === `import` ? VerticalAlignment.Top : VerticalAlignment.Center,
        }}
        on={{
          change: (ev) => helpers.handleModalClose(ev),
        }}
      >
        <div sel='.mp-modal-content'>
          <div sel='.mp-confirm-title'>{helpers.getModalTitle()}</div>
          <div sel='.section-title'>{t(`map-lookup-table.lookup-table`)}</div>
          {state.selectTableErrorMessage ? <div sel='.input-row-error'>{state.selectTableErrorMessage}</div> : null}

          <div sel='.input-row'>
            <div sel='.select-group' class={{hidden: state.hideSelect || this.attr(`type`) === `import`}}>
              <SelectEl
                attrs={{
                  'selected-option': helpers.getSelectedLabel(),
                  'search-placeholder': t(`common.search`),
                  disabled: Boolean(state.fileProcessingDescription),
                  elref: `lookupTableSelect`,
                }}
                props={{
                  sections: helpers.getLookupTableSections(),
                }}
                on={{
                  change: helpers.selectLookupTable,
                }}
              />
              <div sel='.or'>or</div>
            </div>
            <FileUploaderEl
              attrs={helpers.getFileUploadBlockAttrs()}
              on={{
                change: helpers.uploadLookupTableCSV,
                delete: helpers.handleDeleteUpload,
              }}
            />
          </div>
          <div sel='.section-title'>{t(`map-lookup-table.map-to-prop`)}</div>
          {state.invalidPropertyErrorMessage ? (
            <div sel='.input-row-error'>{state.invalidPropertyErrorMessage}</div>
          ) : null}
          {state.requireOverwriteConfirmation ? (
            <div sel='.input-row-warning'>{t(`map-lookup-table.existing-mapping`)}</div>
          ) : null}
          <QueryBlockEl
            {...helpers.getQueryBlockProps()}
            onSelect={helpers.handleSelectProperty}
            onRemove={helpers.handleRemoveProperty}
          />
          <div sel='.button-row'>
            <div sel='.info-link'>
              <a
                attrs={{
                  href: `https://help.mixpanel.com/hc/en-us/articles/360044139291-Lookup-tables`,
                  target: `_blank`,
                }}
                on={{
                  click: helpers.trackInfoClick,
                }}
              >
                <SvgIconEl attrs={{icon: `info`}} />
                {t(`map-lookup-table.learn-more`)}
              </a>
            </div>
            <div sel='.right-buttons'>
              <div sel='.cancel-button-wrapper'>
                <ButtonEl attrs={{theme: ButtonTheme.Tertiary, tabindex: 0}} on={{click: helpers.handleCancel}}>
                  {t(`common.cancel`)}
                </ButtonEl>
              </div>
              <ButtonEl
                attrs={{
                  theme: ButtonTheme.Primary,
                  disabled: helpers.shouldDisableConfirm(),
                  icon: state.savePending ? `spinner` : ``,
                  tabindex: 0,
                  elref: `applyMappingButton`,
                }}
                on={{click: helpers.handleConfirmClick}}
              >
                {helpers.getConfirmButtonLabel()}
              </ButtonEl>
            </div>
          </div>
          <ConfirmEl
            attrs={{
              open: state.overwriteModalOpen,
              closeable: false,
              'remove-host-style': true,
              'grow-to-content': true,
              'not-fullscreen': true,
              'modal-type': `popup`,
              title: t(`map-lookup-table.overwrite`),
              description: t(`map-lookup-table.overwrite-description`),
              theme: THEME.CONFIRM,
              'confirm-button-label': t(`common.confirm`),
            }}
            on={{
              submit: helpers.handleConfirmationModal,
            }}
          />
        </div>
      </ModalEl>
    </div>
  );
}
